import React, { useMemo, useState } from 'react';
import { Spin, Switch, Tag, Tooltip, Button, Empty } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { useProyectoOperacionQuery } from '../../../store/api';
import { useGetDnsStatusQuery } from '../../../store/api';
import HealthBadge, { healthColor } from './HealthBadge';
import IssuesList from './IssuesList';

dayjs.extend(relativeTime);
dayjs.locale('es');

const statusTag = (status) => {
  const map = {
    ok: 'success',
    online: 'success',
    running: 'processing',
    warning: 'warning',
    stale: 'warning',
    unknown: 'default',
    external: 'default',
    offline: 'error',
    error: 'error',
    critical: 'error',
  };
  const labels = { external: 'proveedor externo' };
  return <Tag color={map[status] || 'default'}>{labels[status] || status || '—'}</Tag>;
};

function Card({ title, children, extra }) {
  return (
    <div className="yzen-card" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 className="yzen-card-title" style={{ margin: 0 }}>{title}</h3>
        {extra}
      </div>
      {children}
    </div>
  );
}

export default function ProyectoOperacionPanel({ proyectoId, polling = true }) {
  const navigate = useNavigate();
  const { data, isLoading, isFetching } = useProyectoOperacionQuery(proyectoId, {
    skip: !proyectoId,
    pollingInterval: polling ? 30000 : 0,
  });
  const { data: dns } = useGetDnsStatusQuery(undefined, { pollingInterval: 60000 });
  const [soloProblemas, setSoloProblemas] = useState(false);

  const procesosFiltrados = useMemo(() => {
    const list = data?.procesos || [];
    if (!soloProblemas) return list;
    return list.filter((p) => !['ok', 'success'].includes(p.last_status));
  }, [data, soloProblemas]);

  if (isLoading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin tip="Cargando operación..." />
      </div>
    );
  }
  if (!data?.success) {
    return <Empty description="No se pudo cargar la operación del proyecto" />;
  }

  const { proyecto, salud, servidores, servicios, syncs, backups, bases_datos, urls, ssl, alertas_recientes, roadmap_pendientes, bitacora } = data;
  const urlProd = proyecto?.url_produccion || '';

  return (
    <div>
      <Card
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <HealthBadge estado={salud?.estado} />
            <span>{proyecto?.nombre}</span>
            {isFetching ? <Spin size="small" /> : null}
          </span>
        }
        extra={
          <Button size="small" type="primary" onClick={() => navigate(`/proyecto/${proyectoId}`)}>
            Ver proyecto
          </Button>
        }
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13, color: '#596882' }}>
          <div>Estado: <strong style={{ color: healthColor(salud?.estado) }}>{salud?.estado}</strong></div>
          <div>Issues: {salud?.contadores?.issues || 0}</div>
          <div>Procesos: {salud?.contadores?.procesos || 0}</div>
          {urlProd ? (
            <div>
              URL:{' '}
              <a href={urlProd} target="_blank" rel="noreferrer">{urlProd}</a>
            </div>
          ) : null}
          {dns?.target ? <div>DNS target: {dns.target}</div> : null}
          <div>Verificado: {data.generated_at ? dayjs(data.generated_at).fromNow() : '—'}</div>
        </div>
        <div style={{ marginTop: 12 }}>
          <IssuesList issues={salud?.issues || []} max={8} />
        </div>
      </Card>

      <Card title="Servidores">
        {!servidores?.length ? (
          <p style={{ color: '#8392a5', fontSize: 13 }}>Sin roles de servidor asignados.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#8392a5' }}>
                  <th>Rol</th><th>Nombre</th><th>IP</th><th>Estado</th><th>Carga</th><th>Disco</th><th>Latencia</th><th>Último</th>
                </tr>
              </thead>
              <tbody>
                {servidores.map((s) => (
                  <tr key={`${s.rol_key}-${s.id}`} style={{ borderTop: '1px solid #f5f6fa' }}>
                    <td>{s.rol}</td>
                    <td>
                      <Link to={`/metricas?id=${s.id}`}>{s.nombre || s.id}</Link>
                    </td>
                    <td>{s.ip}</td>
                    <td>{statusTag(s.estado)}</td>
                    <td>{s.carga || '—'}</td>
                    <td>{s.espaciolibre || '—'} / {s.disco || '—'}</td>
                    <td>{s.latencia || '—'}</td>
                    <td>{s.fechaupdatedsincro ? dayjs(s.fechaupdatedsincro).fromNow() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="Crons / tareas"
        extra={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
            Solo problemas <Switch size="small" checked={soloProblemas} onChange={setSoloProblemas} />
            <Link to={`/procesos?proyecto_id=${proyectoId}`}>Ver todos</Link>
          </span>
        }
      >
        {!data.procesos?.length ? (
          <p style={{ color: '#8392a5', fontSize: 13 }}>
            Sin crons vinculados. Crea una regla o asigna desde /procesos.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#8392a5' }}>
                  <th>Tipo</th><th>Script</th><th>Qué hace</th><th>Servidor</th><th>Estado</th><th>Último éxito</th><th>Origen</th>
                </tr>
              </thead>
              <tbody>
                {procesosFiltrados.map((p) => (
                  <tr key={p.id} style={{ borderTop: '1px solid #f5f6fa' }}>
                    <td>{p.process_type}</td>
                    <td>
                      <Tooltip title={p.script_path}>
                        <span>{(p.script_path || p.description_human || '').slice(0, 48)}</span>
                      </Tooltip>
                    </td>
                    <td>
                      <Tooltip title={p.description_human || ''}>
                        <span>{(p.description_human || '—').slice(0, 70)}</span>
                      </Tooltip>
                    </td>
                    <td>{p.server_name || p.server_ip}</td>
                    <td>{statusTag(p.last_status)}</td>
                    <td>{p.last_success_at ? dayjs(p.last_success_at).fromNow() : '—'}</td>
                    <td>{p.proyecto_origen || p.origen || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Servicios">
        {!servicios?.length ? (
          <p style={{ color: '#8392a5', fontSize: 13 }}>Sin servicios vinculados.</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {servicios.map((s) => (
              <li key={s.id}>
                {statusTag(s.last_status)} {s.script_path || s.description_human} — {s.server_name || s.server_ip}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Syncs y backups">
        {!syncs?.length && !backups?.length ? (
          <p style={{ color: '#8392a5', fontSize: 13 }}>Sin syncs/backups vinculados a este proyecto.</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {(syncs || []).map((s) => (
              <li key={s.id}>{statusTag(s.status)} Sync {s.name}</li>
            ))}
            {(backups || []).map((b) => (
              <li key={b.id}>{statusTag(b.status)} Backup {b.name} @ {b.server}</li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Bases de datos">
        {!bases_datos?.length ? (
          <p style={{ color: '#8392a5', fontSize: 13 }}>Sin bases registradas. Configúralas en la ficha del proyecto.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#8392a5' }}>
                  <th>BD</th><th>Servidor</th><th>Rol</th><th>IO/SQL</th><th>Lag</th><th>Estado host</th>
                </tr>
              </thead>
              <tbody>
                {bases_datos.map((b) => (
                  <tr key={b.id} style={{ borderTop: '1px solid #f5f6fa' }}>
                    <td>{b.nombre_bd || '—'}</td>
                    <td>{b.servidor_nombre} ({b.servidor_ip})</td>
                    <td>{b.rol}</td>
                    <td>
                      {b.replica ? `${b.replica.io_running || '?'}/${b.replica.sql_running || '?'}` : '—'}
                    </td>
                    <td>{b.replica?.seconds_behind ?? '—'}</td>
                    <td>{statusTag(b.servidor_estado)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="URLs / SSL">
        {!urls?.length && !ssl?.length ? (
          <p style={{ color: '#8392a5', fontSize: 13 }}>Sin monitores URL/SSL.</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {(urls || []).map((u) => (
              <li key={`u-${u.id}`}>{statusTag(u.ultimo_estado)} {u.nombre || u.url}</li>
            ))}
            {(ssl || []).map((s) => (
              <li key={`s-${s.id}`}>SSL {s.dominio}: {s.dias_restantes ?? '?'} días</li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Alertas, roadmap y bitácora">
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))' }}>
          <div>
            <strong style={{ fontSize: 12, color: '#8392a5' }}>Alertas 7d</strong>
            {!alertas_recientes?.length ? (
              <p style={{ fontSize: 12, color: '#8392a5' }}>Ninguna</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                {alertas_recientes.slice(0, 5).map((a) => (
                  <li key={a.id}>{a.mensaje || a.alerta_nombre}</li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <strong style={{ fontSize: 12, color: '#8392a5' }}>Roadmap</strong>
            {!roadmap_pendientes?.length ? (
              <p style={{ fontSize: 12, color: '#8392a5' }}>Sin pendientes</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                {roadmap_pendientes.slice(0, 5).map((t) => (
                  <li key={t.id}>{t.titulo} ({t.prioridad})</li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <strong style={{ fontSize: 12, color: '#8392a5' }}>Bitácora</strong>
            {!bitacora?.length ? (
              <p style={{ fontSize: 12, color: '#8392a5' }}>Vacía</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                {bitacora.map((b) => (
                  <li key={b.id}>{(b.titulo || b.descripcion || '').slice(0, 60)}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
